'use client'

import { createContext, useContext, useEffect, useMemo } from 'react'
import { useSession } from 'next-auth/react'

export type ActiveRole =
  | 'user'
  | 'buyer'
  | 'head'
  | 'admin'
  | 'vendor'
  | 'auction_committee'
  | 'direct_admin'
  | 'hr_kam'
  | 'gcc_sm'
  | 'gcc_tpdd'
  | 'gcc_mdg'
  | 'cft_member'
  | 'vom'
  | 'approver'

export const ACTIVE_ROLE_LABELS: Record<ActiveRole, string> = {
  admin: 'Admin',
  user: 'User',
  buyer: 'Buyer',
  head: 'Procurement Head',
  vendor: 'Vendor',
  auction_committee: 'Auction Committee',
  direct_admin: 'Direct Procurement Admin',
  hr_kam: 'HR (KAM)',
  gcc_sm: 'GCC SM',
  gcc_tpdd: 'GCC TPDD',
  gcc_mdg: 'GCC MDG',
  cft_member: 'CFT member',
  vom: 'VOM governance',
  approver: 'Approver (Finance, Compliance, CEO)',
}

export type AppTheme = 'supervity' | 'adani'

/** Every user sees the Adani theme. */
const APP_THEME: AppTheme = 'adani'

const BUYER_ROLES = [
  'procurement_buyer',
  'procurement_team',
  'adani_procurement_officer',
]
const HEAD_ROLES = ['procurement_head']
const VENDOR_ROLES = ['vendor', 'supplier']
const COMMITTEE_ROLES = ['auction_committee', 'procurement_auction_committee_member']
const DIRECT_ADMIN_ROLES = ['direct_procurement_admin']
const ONBOARDING_ROLE_MAP: [string[], ActiveRole][] = [
  [['hr_kam'], 'hr_kam'],
  [['gcc_sm'], 'gcc_sm'],
  [['gcc_tpdd', 'tpdd_partner'], 'gcc_tpdd'],
  [['gcc_mdg'], 'gcc_mdg'],
  [['cft_member'], 'cft_member'],
  [['vom_governance'], 'vom'],
  [['finance_approver', 'compliance_approver', 'ceo', 'adani_approver'], 'approver'],
]

/** Role implied by the Keycloak roles on the session. */
export function deriveRole(roles?: string[]): ActiveRole {
  if (roles?.includes('admin')) return 'admin'
  if (roles?.some((r) => DIRECT_ADMIN_ROLES.includes(r))) return 'direct_admin'
  if (roles?.some((r) => COMMITTEE_ROLES.includes(r))) return 'auction_committee'
  if (roles?.some((r) => HEAD_ROLES.includes(r))) return 'head'
  if (roles?.some((r) => BUYER_ROLES.includes(r))) return 'buyer'
  if (roles?.some((r) => VENDOR_ROLES.includes(r))) return 'vendor'
  for (const [names, role] of ONBOARDING_ROLE_MAP) {
    if (roles?.some((r) => names.includes(r))) return role
  }
  return 'user'
}

interface ActiveRoleContextValue {
  /** The signed-in user's role, from their Keycloak roles. */
  activeRole: ActiveRole
  isAdmin: boolean
  theme: AppTheme
}

const ActiveRoleContext = createContext<ActiveRoleContextValue | undefined>(undefined)

export function ActiveRoleProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession()
  const activeRole = deriveRole(session?.roles)

  useEffect(() => {
    document.documentElement.dataset.theme = APP_THEME
  }, [])

  const value = useMemo<ActiveRoleContextValue>(
    () => ({ activeRole, isAdmin: activeRole === 'admin', theme: APP_THEME }),
    [activeRole]
  )

  return <ActiveRoleContext.Provider value={value}>{children}</ActiveRoleContext.Provider>
}

export function useActiveRole(): ActiveRoleContextValue {
  const ctx = useContext(ActiveRoleContext)
  if (!ctx) throw new Error('useActiveRole must be used within an ActiveRoleProvider')
  return ctx
}
